/**
 * Auth API — direct fetch to Edge Function (no auth headers needed, --no-verify-jwt)
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string
const API_BASE = `${SUPABASE_URL}/functions/v1/api/v1`

interface AuthResponse {
  access_token: string
  refresh_token: string
  expires_in: number
  user: Record<string, unknown>
}

async function callApi(path: string, body: Record<string, unknown>): Promise<AuthResponse> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  const data = await res.json().catch(() => null)

  if (!res.ok) {
    const message = data?.error ?? `HTTP ${res.status}`
    throw new Error(message)
  }

  return data as AuthResponse
}

export async function telegramAuth(initData: string): Promise<AuthResponse> {
  return callApi('/auth/telegram', { init_data: initData })
}

export async function verifyCode(sessionId: string, code: string): Promise<AuthResponse> {
  return callApi('/auth/verify-code', { session_id: sessionId, code })
}

export async function refreshToken(token: string): Promise<AuthResponse> {
  return callApi('/auth/refresh', { refresh_token: token })
}

export function saveTokens(accessToken: string, refreshTokenValue: string): void {
  localStorage.setItem('turnifly_access_token', accessToken)
  localStorage.setItem('turnifly_refresh_token', refreshTokenValue)

  // Sync update — supabase.ts fetch wrapper also reads localStorage as fallback
  import('@turnifly/shared/api').then(({ setSupabaseAccessToken }) => {
    setSupabaseAccessToken(accessToken)
  })
}

export function getSavedTokens(): { accessToken: string | null; refreshToken: string | null } {
  return {
    accessToken: localStorage.getItem('turnifly_access_token'),
    refreshToken: localStorage.getItem('turnifly_refresh_token'),
  }
}

export function clearTokens(): void {
  localStorage.removeItem('turnifly_access_token')
  localStorage.removeItem('turnifly_refresh_token')

  // Clear in-memory ref too — supabase.ts fetch checks localStorage fallback
  import('@turnifly/shared/api').then(({ setSupabaseAccessToken }) => {
    setSupabaseAccessToken(null)
  })
}

export async function completeOnboarding(fullName: string, gender: string): Promise<void> {
  const { accessToken } = getSavedTokens()
  if (!accessToken) throw new Error('Not authenticated')

  const res = await fetch(`${API_BASE}/auth/complete-onboarding`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ access_token: accessToken, full_name: fullName, gender }),
  })

  if (!res.ok) {
    const data = await res.json().catch(() => null)
    throw new Error(data?.error ?? 'Failed to complete onboarding')
  }
}
